import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { step1Schema, type Step1Data } from '@/lib/validations';

interface Step1ContactProps {
  initialData?: Partial<Step1Data>;
  onSubmit: (data: Step1Data) => Promise<void>;
  isSubmitting: boolean;
}

export function Step1Contact({ initialData, onSubmit, isSubmitting }: Step1ContactProps) {
  const [formData, setFormData] = useState<Step1Data>({
    full_name: initialData?.full_name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Step1Data, string>>>({});
  const [emailWarning, setEmailWarning] = useState<string | null>(null);

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'phone') {
      processedValue = formatPhone(value);
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    if (errors[name as keyof Step1Data]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Check for .gov/.edu email
    if (name === 'email') {
      const isGovEdu = value.endsWith('.gov') || value.endsWith('.edu') || value.includes('.gov.') || value.includes('.edu.');
      setEmailWarning(value && !isGovEdu ? 'Not a government or education email, but that\'s okay!' : null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = step1Schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof Step1Data, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof Step1Data;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    await onSubmit(result.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="full_name" className="text-foreground text-sm font-medium">
          Full Name *
        </Label>
        <Input
          id="full_name"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          placeholder="John Smith"
          className={`mt-1.5 ${errors.full_name ? 'border-destructive' : ''}`}
        />
        {errors.full_name && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.full_name}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="email" className="text-foreground text-sm font-medium">
          Work Email *
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@agency.gov"
          className={`mt-1.5 ${errors.email ? 'border-destructive' : ''}`}
        />
        {errors.email && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.email}
          </p>
        )}
        {emailWarning && !errors.email && (
          <p className="text-muted-foreground text-xs mt-1">{emailWarning}</p>
        )}
      </div>

      <div>
        <Label htmlFor="phone" className="text-foreground text-sm font-medium">
          Phone Number *
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(555) 123-4567"
          className={`mt-1.5 ${errors.phone ? 'border-destructive' : ''}`}
        />
        {errors.phone && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.phone}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gold hover:bg-gold-light text-dark-bg font-semibold py-5 text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold/20"
      >
        {isSubmitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            Continue to Next Step
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
}
